/**
 * Shim module for `@intrasync/utils` legacy namespace-style imports.
 *
 * The published `@intrasync/utils` package exposes functions as individual
 * named exports. Historically the codebase imported a `utils` namespace
 * (`import { utils } from "@intrasync/utils"`). This shim re-creates that
 * namespace plus a few helpers that were removed from the package.
 */

import { reaction, IReactionDisposer } from "mobx";
import * as utilsExports from "@intrasync/utils";

/** Convert kebab/uri-style service name to a store name (e.g. "sales-orders" -> "salesOrdersStore"). */
const servicePathToStoreName = (servicePath: string): string => {
  const camelCased = servicePath.replace(/-(.)/g, (_match: string, chr: string) =>
    chr.toUpperCase()
  );
  return `${camelCased}Store`;
};

export const utils = {
  ...utilsExports,
  servicePathToStoreName,
};

/**
 * Watch a computed property on a MobX observable. Fires on change.
 * Returns a disposer that cleans up the reaction.
 */
export const watchComputed = <T extends object, K extends keyof T>(
  target: T,
  key: K
): IReactionDisposer => {
  return reaction(
    () => target[key],
    () => {
      /* no-op; caller listens via separate reactions */
    }
  );
};

/** Minimal CSV export helper. */
export const exportDataToCsv = (
  filename: string,
  data: Record<string, unknown>[]
): void => {
  if (!data || data.length === 0) return;
  const headers = Object.keys(data[0]);
  const rows = data.map((row) =>
    headers
      .map((h) => {
        const v = row[h];
        const s = v == null ? "" : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      })
      .join(",")
  );
  const csv = [headers.join(","), ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename.endsWith(".csv") ? filename : `${filename}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
